import React, { useState } from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@tanstack/react-query';

import {
  AdminRole,
  CreatedUser,
  CreateUserPayload,
  adminUsersApi,
} from '../../../../api/adminUsersApi';
import { RbacRole, rolesV2Api } from '../../../../api/rolesV2Api';
import { colors, tableCellSx } from '../../../../theme/tokens';
import { WriteIconButton } from '../../../../components/ui';

/**
 * Inline new-row insert for the Users tab. Render via parent as a table
 * row positioned at the bottom of the list — per Designer B's brief we
 * avoid a modal dialog for the common "invite a teammate" flow.
 */
interface Props {
  roles: AdminRole[];
  /**
   * RBAC_V2 role catalog (CPT-1363). When supplied — the V2 Users tab — the
   * dropdown lists THESE, so the invite options match the Roles tab. Omitted on
   * the legacy page, which keeps listing `roles`.
   *
   * The two catalogs are genuinely different tables: legacy `config.app_role` /
   * `app_user_role` vs v2 `config.app_role_v2` / `app_user_role_v2`. That is why
   * the invite dropdown offered roles (Flex User, Release Manager, Workflow
   * Creator, …) that do not exist on the Roles tab, while the Role column —
   * already bound to the v2 catalog — showed the right ones.
   */
  rolesV2?: RbacRole[];
  onCancel: () => void;
  onCreated: (row: CreatedUser) => void;
  onError: (msg: string) => void;
}

const AddUserInlineRow: React.FC<Props> = ({ roles, rolesV2, onCancel, onCreated, onError }) => {
  // Which catalog the dropdown offers. Normalised to {code,name} so the JSX
  // does not branch.
  const usingV2 = Array.isArray(rolesV2);
  const options: { code: string; name: string }[] = usingV2
    ? (rolesV2 as RbacRole[]).map((r) => ({ code: r.role_code, name: r.role_name }))
    : roles.map((r) => ({ code: r.role_code, name: r.role_name }));
  const [form, setForm] = useState<CreateUserPayload>({
    email: '',
    display_name: '',
    role_code: options.find((r) => r.code === 'default')?.code ?? options[0]?.code ?? '',
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      // config.app_user_role still has a FK to the LEGACY role table, so the
      // create call must carry a legacy code while both systems coexist.
      // 'default' is a system role in both catalogs, so it is always valid and
      // is the v2 read-time fallback anyway.
      const row = await adminUsersApi.createUser({
        email: form.email.trim(),
        display_name: form.display_name?.trim() || null,
        role_code: usingV2 ? 'default' : form.role_code,
      });
      // Then apply the v2 role the admin actually picked (writes
      // app_user_role_v2 — the table the Users list and Roles tab read).
      if (usingV2 && form.role_code && form.role_code !== 'default') {
        await rolesV2Api.setUserRole(row.id, form.role_code);
      }
      return row;
    },
    onSuccess: (row) => onCreated(row),
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'conflict') onError('A user with this email already exists.');
      else if (typeof code === 'string') onError(`Invite failed: ${code}`);
      else onError('Invite failed. Try again.');
    },
  });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit = emailValid && !!form.role_code;

  return (
    <TableRow sx={{ backgroundColor: colors.adminAccentSubtle }}>
      <TableCell sx={tableCellSx}>
        <TextField
          size="small"
          type="email"
          placeholder="user@example.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          error={!!form.email && !emailValid}
          inputProps={{ maxLength: 200 }}
          autoFocus
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <TextField
          size="small"
          placeholder="Display name (optional)"
          value={form.display_name ?? ''}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          inputProps={{ maxLength: 120 }}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <TextField
          size="small"
          select
          value={form.role_code}
          onChange={(e) => setForm({ ...form, role_code: e.target.value })}
        >
          {options.map((r) => (
            <MenuItem key={r.code} value={r.code}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={tableCellSx} />
      <TableCell sx={tableCellSx} />
      <TableCell sx={tableCellSx}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Invite user (Enter)">
            <span>
              <WriteIconButton ignoreEnvReadOnly
                size="small"
                aria-label="Invite user"
                disabled={!canSubmit || createMutation.isPending}
                onClick={() => createMutation.mutate()}
                sx={{ color: colors.successIcon }}
              >
                <CheckIcon fontSize="small" />
              </WriteIconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel (Esc)">
            <IconButton size="small" onClick={onCancel} sx={{ color: colors.textSecondary }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default AddUserInlineRow;
